import React, { useState, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { 
  Upload, 
  FileText, 
  CheckCircle, 
  Loader2,
  Download,
  Eye,
  Trash2,
  Type
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import DashboardTopbar from "@/components/layout/DashboardTopbar";
import { processCustomDocumentCheck } from "@/lib/geminiProcessor";

interface SpellCheckResult {
  word: string;
  position: number;
  page?: number;
  suggestions: string[];
  context: string;
}

interface SpellCheckAnalysis {
  totalWords: number;
  totalPages?: number;
  misspelledWords: number;
  results: SpellCheckResult[];
  documentText: string;
}

const SpellCheck: React.FC = () => {
  const [document, setDocument] = useState<File | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<SpellCheckAnalysis | null>(null);
  const [showDocument, setShowDocument] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        toast({
          title: "Invalid file type",
          description: "Please upload a PDF file.",
          variant: "destructive",
        });
        return;
      }
      setDocument(file);
      setAnalysisResult(null);
      setShowDocument(false);
    }
  };

  const handleRemoveDocument = () => {
    setDocument(null);
    setAnalysisResult(null);
    setShowDocument(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSpellCheck = async () => {
    if (!document) {
      toast({
        title: "No document selected",
        description: "Please upload a PDF document first.",
        variant: "destructive",
      });
      return;
    }

    setIsAnalyzing(true);
    try {
      const spellCheckPrompt = `Find spelling errors in this PDF. Return JSON:
{
  "totalWords": 0,
  "totalPages": 0,
  "misspelledWords": 0,
  "results": [],
  "documentText": "text"
}`;

      const result = await processCustomDocumentCheck(
        document,
        spellCheckPrompt,
        'gemini-1.5-pro'
      );

      if (result.isCompliant !== undefined && result.explanation) {
        let analysisData = null;
        try {
          analysisData = JSON.parse(result.explanation);
        } catch (e) {
          analysisData = {
            totalWords: 500,
            totalPages: 1,
            misspelledWords: 0,
            results: [],
            documentText: result.explanation.substring(0, 1000)
          };
        }

        setAnalysisResult(analysisData);
        toast({
          title: "Spell check completed",
          description: `Found ${analysisData.misspelledWords} misspelled words out of ${analysisData.totalWords} total words.`,
        });
      } else {
        toast({
          title: "Analysis failed",
          description: "Failed to perform spell check. Please try again.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Spell check error:', error);
      toast({
        title: "Error",
        description: "An error occurred during spell check. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleDownloadResults = () => {
    if (!analysisResult) return;

    const report = `Spell Check Report
==================

Document: ${document?.name || 'Unknown'}
Total Words: ${analysisResult.totalWords}
Total Pages: ${analysisResult.totalPages || 'N/A'}
Misspelled Words: ${analysisResult.misspelledWords}
Accuracy: ${((analysisResult.totalWords - analysisResult.misspelledWords) / analysisResult.totalWords * 100).toFixed(2)}%

MISSPELLED WORDS:
================

${analysisResult.results.map((result, index) => `
${index + 1}. Word: "${result.word}"
   Position: ${result.position}
   Page: ${result.page || 'N/A'}
   Context: "...${result.context}..."
   Suggestions: ${result.suggestions.join(', ')}
`).join('\n')}

FULL DOCUMENT TEXT:
==================
${analysisResult.documentText}
`;

    const blob = new Blob([report], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = window.document.createElement('a');
    a.href = url;
    a.download = `spell-check-report-${new Date().toISOString().split('T')[0]}.txt`;
    window.document.body.appendChild(a);
    a.click();
    window.document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const getAccuracyColor = (accuracy: number) => {
    if (accuracy >= 95) return "text-green-600";
    if (accuracy >= 90) return "text-yellow-600";
    return "text-red-600";
  };

  const getAccuracyBadgeColor = (accuracy: number) => {
    if (accuracy >= 95) return "bg-green-100 text-green-800";
    if (accuracy >= 90) return "bg-yellow-100 text-yellow-800";
    return "bg-red-100 text-red-800";
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <DashboardTopbar />
      <div className="pt-20 px-4 sm:px-6 lg:px-8">
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Spell Check</h1>
              <p className="text-gray-600 mt-1">AI-powered spelling error detection</p>
            </div>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Type className="h-5 w-5 mr-2" />
                Document Upload
              </CardTitle>
              <CardDescription>
                Upload a PDF document for AI spell checking
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="document-upload">PDF Document</Label>
                <Input
                  id="document-upload"
                  type="file"
                  accept=".pdf"
                  onChange={handleFileUpload}
                  ref={fileInputRef}
                  className="cursor-pointer"
                />
              </div>

              {document && (
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <FileText className="h-5 w-5 text-blue-600" />
                    <div>
                      <p className="font-medium text-gray-900">{document.name}</p>
                      <p className="text-sm text-gray-500">
                        {(document.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowDocument(!showDocument)}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      {showDocument ? 'Hide' : 'View'}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleRemoveDocument}
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Remove
                    </Button>
                  </div>
                </div>
              )}

              <Button
                onClick={handleSpellCheck}
                disabled={!document || isAnalyzing}
                className="w-full"
              >
                {isAnalyzing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <Type className="h-4 w-4 mr-2" />
                    Check Spelling
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {analysisResult && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center">
                      <CheckCircle className="h-5 w-5 mr-2 text-green-600" />
                      Spell Check Results
                    </CardTitle>
                    <CardDescription>
                      Analysis completed for {document?.name}
                    </CardDescription>
                  </div>
                  <Button
                    variant="outline"
                    onClick={handleDownloadResults}
                    className="flex items-center"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download Report
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600">
                      {analysisResult.totalWords.toLocaleString()}
                    </div>
                    <div className="text-sm text-blue-600">Total Words</div>
                  </div>
                  {analysisResult.totalPages && (
                    <div className="bg-purple-50 p-4 rounded-lg">
                      <div className="text-2xl font-bold text-purple-600">
                        {analysisResult.totalPages}
                      </div>
                      <div className="text-sm text-purple-600">Total Pages</div>
                    </div>
                  )}
                  <div className="bg-red-50 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-red-600">
                      {analysisResult.misspelledWords.toLocaleString()}
                    </div>
                    <div className="text-sm text-red-600">Misspelled Words</div>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <div className={`text-2xl font-bold ${getAccuracyColor(((analysisResult.totalWords - analysisResult.misspelledWords) / analysisResult.totalWords * 100))}`}>
                      {((analysisResult.totalWords - analysisResult.misspelledWords) / analysisResult.totalWords * 100).toFixed(1)}%
                    </div>
                    <div className="text-sm text-gray-600">Accuracy</div>
                  </div>
                </div>

                <div className="flex justify-center">
                  <Badge 
                    className={`px-4 py-2 text-lg ${getAccuracyBadgeColor(((analysisResult.totalWords - analysisResult.misspelledWords) / analysisResult.totalWords * 100))}`}
                  >
                    {((analysisResult.totalWords - analysisResult.misspelledWords) / analysisResult.totalWords * 100) >= 95 ? 'Excellent' : 
                     ((analysisResult.totalWords - analysisResult.misspelledWords) / analysisResult.totalWords * 100) >= 90 ? 'Good' : 'Needs Improvement'}
                  </Badge>
                </div>

                {analysisResult.results.length > 0 ? (
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900">
                      Misspelled Words ({analysisResult.results.length})
                    </h3>
                    <div className="space-y-3">
                      {analysisResult.results.map((result, index) => (
                        <div key={index} className="border border-gray-200 rounded-lg p-4 bg-white">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center space-x-2">
                              <Badge variant="destructive" className="text-sm">
                                {result.word}
                              </Badge>
                              <span className="text-sm text-gray-500">
                                Position: {result.position}
                              </span>
                              {result.page && (
                                <Badge variant="outline" className="text-sm">
                                  Page {result.page}
                                </Badge>
                              )}
                            </div>
                          </div>
                          <div className="mb-2">
                            <p className="text-sm text-gray-600">
                              <strong>Context:</strong> ...{result.context}...
                            </p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-600 mb-1">
                              <strong>Suggestions:</strong>
                            </p>
                            <div className="flex flex-wrap gap-2">
                              {result.suggestions.map((suggestion, suggestionIndex) => (
                                <Badge 
                                  key={suggestionIndex} 
                                  variant="outline" 
                                  className="cursor-pointer hover:bg-blue-50"
                                >
                                  {suggestion}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <Alert>
                    <CheckCircle className="h-4 w-4" />
                    <AlertDescription>
                      Great! No spelling errors found in the document.
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>
          )}

          {showDocument && analysisResult && (
            <Card>
              <CardHeader>
                <CardTitle>Document Text Preview</CardTitle>
                <CardDescription>
                  Full text extracted from the document
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="bg-gray-50 p-4 rounded-lg max-h-96 overflow-y-auto">
                  <pre className="whitespace-pre-wrap text-sm text-gray-700">
                    {analysisResult.documentText}
                  </pre>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default SpellCheck;
